/**
 * **Where a deadline lands when the trainer gives a goal more time.**
 *
 * `PATCH /customer/{id}/goal/{goalId}` refuses a deadline that is not in the future after UTC
 * normalisation, and `GoalRenewalDueDetector` raises the task for a deadline within seven days OR
 * already past. So "+2 tygodnie" cannot mean "the deadline plus fourteen days" on every goal: a
 * goal three weeks overdue would be moved to a day that is still a week behind, the server would
 * refuse it, and the trainer would have pressed a button that could never work.
 *
 * The extension therefore counts from whichever is LATER — the current deadline or today. A goal
 * still ahead of its date keeps its rhythm (the 30th becomes the 14th); an overdue one gets the
 * full term from now.
 *
 * Pure: civil days in, a civil day out. No clock of its own.
 */

import { addDays, parseIsoDate, toIsoDate } from '~/shared/lib/calendarDate';

/**
 * The new deadline, as a civil day, or `null` where either day is unreadable.
 *
 * @param deadline the goal's current deadline — a civil day or an instant; only its day is read.
 * @param todayIso the trainer's own today.
 */
export function extendedGoalDeadline(
  deadline: string,
  todayIso: string,
  weeks: number,
): string | null {
  const current = parseIsoDate(deadline.slice(0, 10));
  const today = parseIsoDate(todayIso);
  if (current === null || today === null) return null;
  const from = current.getTime() > today.getTime() ? current : today;
  return toIsoDate(addDays(from, weeks * 7));
}
